import React from 'react'
import { useState, useEffect, useRef } from "react"
import { X } from "lucide-react"
import { toast } from "react-toastify"
import { updateUserPassword } from './AsianExchangeSrc/api'
import Loader from './AsianExchangeSrc/utils/Loader'
import { useSelector } from 'react-redux'
import { selectSite } from './AsianExchangeSrc/utils/helper/commonSelectors'

export default function ChangePasswordModal({ isOpen, onClose, onSuccess, user }) {
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  })
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const modalRef = useRef(null)
  const topHeaderColor = useSelector(selectSite())?.headerColor;

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, onClose])

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    if (!formData.oldPassword) {
      setError("Old password is required")
      setLoading(false)
      return
    }
    if (!formData.newPassword) {
      setError("New password is required")
      setLoading(false)
      return
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match")
      setLoading(false)
      return
    }
    if (formData.newPassword === formData.oldPassword) {
      setError("Old password and new password cannot be the same")
      setLoading(false)
      return
    }

    try {
      const response = await updateUserPassword({
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      })

      if (!response.success) {
        setError(response?.data?.message || "Failed to update password")
        throw new Error(response?.data.message || "Failed to update password")
      }

      // Success - close dialog and reset form
      setFormData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      })
      toast.success(response?.message || "Password updated successfully")
      onSuccess() // Call the success callback
    } catch (err) {
      setError(err.message || "Error updating password")
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { id, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300">
      {loading && <Loader />}
      <div
        ref={modalRef}
        className="bg-white shadow-2xl w-full max-w-md mx-auto rounded-md overflow-hidden transform transition-all duration-300 ease-in-out"
        style={{ boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
      >
        <div className="text-white p-2 flex justify-between items-center"  style={{
              backgroundImage: `linear-gradient(180deg, ${topHeaderColor?.start}, ${topHeaderColor?.end})`,
            }}>
          <h2 className="text-sm font-semibold">Change Password</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 focus:outline-none transition-colors duration-200 rounded-full p-1 hover:bg-[#238A9D]"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#EDF6F9] p-6 space-y-5">
          {error && (
            <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-md flex items-start">
              <span className="mt-0.5">⚠️</span>
              <span className="ml-2">{error}</span>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="oldPassword" className="text-gray-700 font-medium block">
                Old Password
              </label>
              <input
                id="oldPassword"
                type="password"
                value={formData.oldPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2A9CB0] focus:border-transparent transition-all duration-200"
                disabled={loading}
                placeholder="Enter your current password"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="newPassword" className="text-gray-700 font-medium block">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2A9CB0] focus:border-transparent transition-all duration-200"
                disabled={loading}
                placeholder="Enter your new password"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-gray-700 font-medium block">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2A9CB0] focus:border-transparent transition-all duration-200"
                disabled={loading}
                placeholder="Confirm your new password"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#2A9CB0] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm font-medium"
                          disabled={loading}
                          style={{
                            backgroundImage: `linear-gradient(180deg, ${topHeaderColor?.start}, ${topHeaderColor?.end})`,
                          }}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

